<template>
  <div v-if="model" class="detalle-borrador-panel">
    <DxTabPanel height="95%" tabs-position="top" icon-position="left" :defer-rendering="true">
      <DxItem :key="1" :title="tituloTab1" class="tab-item" :disabled="false" icon="fa-regular fa-file-lines">
        <component :is="datosComponent" :model="model" />
      </DxItem>
      <DxItem :key="2" :title="tituloTab2" class="tab-item" :disabled="false" icon="fa-regular fa-folder-open">
        <borrador-operacion-documentos :documentos="documentos"></borrador-operacion-documentos>
      </DxItem>
      <DxItem :key="3" :title="tituloTab3" class="tab-item" :disabled="false" icon="fa-regular fa-notes">
        <borrador-operacion-notas :notas="notas" :id-borrador-operacion="model.Id" @update:notas="cargarNotasBorrador"></borrador-operacion-notas>
      </DxItem>

    </DxTabPanel>
  </div>
</template>

<script setup lang="ts">
import { useModalBuilder, type ToolbarReference } from "@/.shared/helpers/modal-building-helper";
import { useIntl } from "@/.shared/services/i18n/i18nservice";
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import DxTabPanel, { DxItem } from "devextreme-vue/tab-panel";
import { type DetailBorradorOperacionModel, type DetailNotaBorradorOperacionModel } from "@/.shared/.api";
import { getBorradorOperacionDatosComponent } from "@/components/borradores-operacion/ComponentFactory";
import type { DxButton } from "devextreme-vue";
import { messageBoxResult } from "@/.shared/components/dialogs/message-box";
import { defaultButtonProps } from "@/.shared/helpers/button-helper";
import BorradorOperacionDocumentos from "@/components/borradores-operacion/BorradorOperacionDocumentos.vue";
import type { IDocumentInitialState } from "@/.shared/components/document-card/models/IDocumentCardProps";
import { useRouter } from "@/.shared/services/routing-services";
import { usePermissionCheckers } from "@/services/auth/permission-checkers-bo";
import { useApi } from "@/.shared/.api/integration";
import BorradorOperacionNotas from "@/components/borradores-operacion/BorradorOperacionNotas.vue";

const props = defineProps({
  model: Object as () => DetailBorradorOperacionModel,
});

const intl = useIntl();
const router = useRouter();
const api = useApi();
const notas = ref<DetailNotaBorradorOperacionModel[]>([]);

const documentos = computed<IDocumentInitialState[]>(() => props.model?.ArchivosAdjuntos.map
  (archivo => ({ id: archivo.Id, uri: archivo.Ruta, fileName: archivo.Nombre, description: archivo.Descripcion })) ?? [] as IDocumentInitialState[]);

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!);

  configureButtons(modal);

  await cargarNotasBorrador();
});

const tituloTab1 = computed(() => `${intl.i18n.t("borradores-operacion.form.datos")}`);
const tituloTab2 = computed(() => `${intl.i18n.t("borradores-operacion.form.documentos")}`);
const tituloTab3 = computed(() => `${intl.i18n.t("borradores-operacion.form.notas")}`);

const datosComponent = computed(() => getBorradorOperacionDatosComponent(props.model?.Tipo));

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  addNuevaOperacionButton(modal);
  addEditarOperacionButton(modal);
  addCloseButton(modal);
}

const permissionCheckers = usePermissionCheckers();

const addNuevaOperacionButton = (modal: ReturnType<typeof useModalBuilder>) => {

  if (props.model?.TieneOperacionAsociada || props.model?.FechaBaja != null || !permissionCheckers.canAccessBorradoresOperacion())
    return;

  let btn: ToolbarReference<DxButton>;
  btn = modal.addButton('nueva-operacion', defaultButtonProps(intl.i18n.t("borradores-operacion.ui.buttons.nueva_operacion")), { toolbar: 'bottom', location: 'before' });
  btn.update(w => w.onClick = async () => {
    const url = calculateUrlNuevaVenta(props.model!);
    window.open(url, '_blank');
    modal.close(messageBoxResult.ok);
  });
}

const addEditarOperacionButton = (modal: ReturnType<typeof useModalBuilder>) => {
  if (!props.model?.PuedeEditarOperacion || !permissionCheckers.canAccessBorradoresOperacion())
    return;
    
  let btn: ToolbarReference<DxButton>;
  btn = modal.addButton('editar-operacion', defaultButtonProps(intl.i18n.t("borradores-operacion.ui.buttons.editar_operacion")), { toolbar: 'bottom', location: 'before' });
    btn.update(w => w.onClick = async () => {
      const url = calculateUrlEditarVenta(props.model!);
      window.open(url, '_blank');
      modal.close(messageBoxResult.ok);
    });
}


function addCloseButton(modal: ReturnType<typeof useModalBuilder>) {
  let closeButton: ToolbarReference<DxButton>;
  closeButton = modal.standardButtons.addClose();
  closeButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));
}

const calculateUrlNuevaVenta = (detailBorrador: DetailBorradorOperacionModel) => {
  const rutaBO = import.meta.env.VITE_BASE_URL;

  switch (detailBorrador.Tipo) {
    case 'AGUA':
      return `${rutaBO}/nueva-venta-agua?idBorrador=${detailBorrador.Id}`;
    case 'ENERGIA':
      return `${rutaBO}/nueva-venta?idBorrador=${detailBorrador.Id}`;
  }
}

const calculateUrlEditarVenta = (detailBorrador: DetailBorradorOperacionModel) => {
  const rutaBO = import.meta.env.VITE_BASE_URL;
  switch (detailBorrador.Tipo) {
    case 'AGUA':
      return `${rutaBO}/nueva-venta-agua?idOperacion=${detailBorrador.IdOperacionAsociada}&idBorrador=${detailBorrador.Id}`;
    case 'ENERGIA':
      return `${rutaBO}/nueva-venta?id=${detailBorrador.IdOperacionAsociada}&idBorrador=${detailBorrador.Id}`;
  }
}

const cargarNotasBorrador = async () => {
  notas.value = await api.callStandardApi("getListadoNotasBorradorOperacion", { showLoading: false }, props.model?.Id!);
}

</script>

<style lang="scss" scoped></style>